import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [contactInfo, setContactInfo] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [infoLoading, setInfoLoading] = useState(true);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Fetch contact information from backend
  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await fetch(`${baseURL}/contact-info.php`);
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data);
      } else {
        throw new Error('Failed to fetch contact information');
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
      // Set default values if API fails
      setContactInfo({
        phone: '0799228282',
        email: 'info@precision-lab-solutions.com',
        address: 'Nairobi, Kenya',
        business_hours: "Mon - Fri: 8:00 AM - 5:00 PM\nSat: 9:00 AM - 1:00 PM"
      });
    } finally {
      setInfoLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('http://localhost/backend/api/contact-submit.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        setAlert({
          show: true,
          type: 'success',
          message: 'Thank you for your message! We will get back to you soon.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      setAlert({
        show: true,
        type: 'danger',
        message: error.message || 'Error sending message. Please try again or contact us directly.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Parse business hours with line breaks
  const renderBusinessHours = (hours) => {
    if (!hours) return null;
    return hours.split('\n').map((line, index) => (
      <p key={index} className="mb-0 text-muted">{line}</p>
    ));
  };

  if (infoLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading contact information...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="mb-5">
        <Col>
          <h1 className="text-center royal-blue-text emerald-green-border mb-4">Contact Us</h1>
          <p className="lead text-center">
            Get in touch with our team for laboratory solutions, technical support, or consultation services.
          </p>
        </Col>
      </Row>

      <Row>
        {/* Contact Information */}
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4">
              <h4 className="royal-blue-text mb-4">Get In Touch</h4>
              
              <div className="d-flex mb-3">
                <div className="royal-blue-text me-3">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div>
                  <h6 className="mb-1">Our Location</h6>
                  <p className="mb-0 text-muted">{contactInfo?.address || 'Nairobi, Kenya'}</p>
                </div>
              </div>

              <div className="d-flex mb-3">
                <div className="royal-blue-text me-3">
                  <i className="fas fa-phone"></i>
                </div>
                <div>
                  <h6 className="mb-1">Call Us</h6>
                  <p className="mb-0 text-muted">{contactInfo?.phone || '0799228282'}</p>
                </div>
              </div>

              <div className="d-flex mb-3">
                <div className="royal-blue-text me-3">
                  <i className="fas fa-envelope"></i>
                </div>
                <div>
                  <h6 className="mb-1">Email Us</h6>
                  <p className="mb-0 text-muted">{contactInfo?.email || 'info@precision-lab-solutions.com'}</p>
                </div>
              </div>

              <div className="d-flex">
                <div className="royal-blue-text me-3">
                  <i className="fas fa-clock"></i>
                </div>
                <div>
                  <h6 className="mb-1">Business Hours</h6>
                  {contactInfo?.business_hours ? (
                    renderBusinessHours(contactInfo.business_hours)
                  ) : (
                    <>
                      <p className="mb-0 text-muted">Mon - Fri: 8:00 AM - 5:00 PM</p>
                      <p className="mb-0 text-muted">Sat: 9:00 AM - 1:00 PM</p>
                    </>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Contact Form */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h4 className="royal-blue-text mb-4">Send us a Message</h4>
              
              {alert.show && (
                <Alert variant={alert.type} onClose={() => setAlert({...alert, show: false})} dismissible>
                  {alert.message}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company/Institution</Form.Label>
                      <Form.Control
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Subject *</Form.Label>
                  <Form.Control
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Message *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    placeholder="Please describe your requirements or inquiry in detail..."
                  />
                </Form.Group>

                <Button 
                  variant="primary" 
                  type="submit" 
                  size="lg" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Regional Coverage */}
      <Row className="mt-5">
        <Col>
          <Card className="bg-light border-0">
            <Card.Body className="p-4 text-center">
              <h4 className="royal-blue-text mb-3">Serving East Africa</h4>
              <p className="mb-0">
                We serve clients across Kenya, Uganda, Tanzania, Rwanda, Ethiopia, Somalia, Somaliland, and beyond. 
                Contact us to learn more about our services in your region.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Contact;